/**
 * The words and the pitch of a passage: lyrics, an expression mark, transposition.
 *
 * All three act on the selection, which is why they sit together: select the notes, then say
 * what is sung over them, what is written above them, or how far they move. A line of lyrics
 * is laid one syllable per note -- hyphens split a word into syllables -- and running out of
 * notes is refused rather than silently dropping the end of the line.
 */

import { useState } from "react";
import { INTERVALS, lyrics, marks, transpose } from "@app/score-editor";
import type { Command, Score, Selection, WordsStyle } from "@app/score-editor";
import { Choice, Count, Field } from "./ScoreSetupFields";
import { Kicker } from "./Page";

const CONTROL =
  "h-10 w-full rounded-[10px] border border-rule bg-leaf px-2.5 text-[13px] text-ink outline-none focus:border-ink";

/**
 * The three text styles, with what each one is for.
 *
 * The placeholder is the example a player would recognise the style by, and the side of the
 * staff is the one the editor will put it on: an expression leans under the staff, a
 * technique stands upright over it, a tempo word is bold over it.
 */
const TEXT_STYLES: readonly {
  readonly value: WordsStyle;
  readonly label: string;
  readonly example: string;
  readonly side: string;
}[] = [
  { value: "expression", label: "Expression (italic)", example: "dolce", side: "under" },
  { value: "technique", label: "Technique (upright)", example: "pizz.", side: "over" },
  { value: "tempo", label: "Tempo (bold)", example: "rit.", side: "over" },
];

const BUTTON =
  "h-10 rounded-[10px] border border-rule px-3 text-[12px] text-ink-faded hover:border-ink-quiet hover:text-ink disabled:cursor-default disabled:text-ink-quiet/60";

export interface ScoreInspectorTextProps {
  readonly score: Score;
  readonly selection: Selection;
  edit(built: Command | string, label?: string): void;
}

export default function ScoreInspectorText({
  score,
  selection,
  edit,
}: ScoreInspectorTextProps): JSX.Element {
  const [verse, setVerse] = useState(1);
  const [line, setLine] = useState("");
  const [saying, setSaying] = useState("");
  const [style, setStyle] = useState<WordsStyle>("expression");
  const [interval, setInterval] = useState(INTERVALS[1].name);
  const nothing = selection.events.size === 0;

  const chosen = INTERVALS.find((candidate) => candidate.name === interval) ?? INTERVALS[0];
  const written = TEXT_STYLES.find((candidate) => candidate.value === style) ?? TEXT_STYLES[0];

  const sing = (): void => {
    if (line.trim() === "") return;
    edit(lyrics.typeLine(score, selection, line, verse), "lyrics");
    setLine("");
  };

  const say = (): void => {
    if (saying.trim() === "") return;
    edit(marks.words(score, selection, saying, style), `${style} text`);
    setSaying("");
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Kicker>Lyrics</Kicker>
        <div className="flex items-end gap-2">
          <Count
            label="Verse"
            value={verse}
            min={1}
            max={8}
            className="w-[72px]"
            onCommit={setVerse}
          />
          <Field label="Words for the selection" className="flex-1">
            <input
              value={line}
              placeholder="glo-ri-a in ex-cel-sis"
              disabled={nothing}
              onChange={(event) => setLine(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") sing();
              }}
              className={CONTROL}
            />
          </Field>
        </div>
        <div className="flex gap-2">
          <button type="button" className={BUTTON} disabled={nothing} onClick={sing}>
            Write the words
          </button>
          <button
            type="button"
            className={BUTTON}
            disabled={nothing}
            onClick={() => edit(lyrics.clearLyrics(score, selection, verse), "clear lyrics")}
          >
            Clear verse {verse}
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <Kicker>Text</Kicker>
        <Choice<WordsStyle>
          label="Style"
          value={style}
          options={TEXT_STYLES.map((candidate) => ({
            value: candidate.value,
            label: candidate.label,
          }))}
          onChange={setStyle}
        />
        <Field label={`Written ${written.side} the passage`}>
          <input
            value={saying}
            placeholder={written.example}
            disabled={nothing}
            onChange={(event) => setSaying(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") say();
            }}
            className={CONTROL}
          />
        </Field>
      </div>

      <div className="space-y-2">
        <Kicker>Transpose</Kicker>
        <Choice
          label="Interval"
          value={interval}
          options={INTERVALS.map((candidate) => ({
            value: candidate.name,
            label: candidate.name,
          }))}
          onChange={setInterval}
        />
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={BUTTON}
            disabled={nothing}
            onClick={() => edit(transpose.transpose(score, selection, chosen, 1), "transpose")}
          >
            Selection up
          </button>
          <button
            type="button"
            className={BUTTON}
            disabled={nothing}
            onClick={() => edit(transpose.transpose(score, selection, chosen, -1), "transpose")}
          >
            Selection down
          </button>
          <button
            type="button"
            className={BUTTON}
            onClick={() => edit(transpose.transposeScore(score, chosen, 1), "transpose the score")}
          >
            Score up
          </button>
          <button
            type="button"
            className={BUTTON}
            onClick={() => edit(transpose.transposeScore(score, chosen, -1), "transpose the score")}
          >
            Score down
          </button>
        </div>
      </div>
    </div>
  );
}
